import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import {
    Box,
    Typography,
    TextField,
    Button,
    List,
    ListItem,
    IconButton,
    FormHelperText,
    Paper
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import CancelIcon from '@mui/icons-material/Cancel';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

const ActionPlanSection = ({ onNext, onBack, isFirstSection, formData }) => {
    const [newStep, setNewStep] = useState('');
    const [editingIndex, setEditingIndex] = useState(null);
    const [editedValue, setEditedValue] = useState('');
    const { handleSubmit, formState: { errors }, setValue, watch, reset } = useForm({
        defaultValues: {
            plan_dzialania_kroki: formData?.plan_dzialania_kroki || [],
            plan_dzialania_dodatkowe_mysli: formData?.plan_dzialania_dodatkowe_mysli || ''
        }
    });

    // Update form values when formData changes (when user goes back)
    useEffect(() => {
        if (formData?.plan_dzialania_kroki) {
            reset({
                plan_dzialania_kroki: formData?.plan_dzialania_kroki || [],
                plan_dzialania_dodatkowe_mysli: formData?.plan_dzialania_dodatkowe_mysli || ''
            });
        }
    }, [formData, reset]);

    const steps = watch('plan_dzialania_kroki');
    const goalMappings = {
        "Szukam pierwszej pracy": "do znalezienia pierwszej pracy",
        "Szukam pracy": "do znalezienia pracy",
        "Myślę o znalezieniu innej/nowej pracy": "do decyzji o szukaniu innej pracy",
        "Wracam na rynek pracy": "w powrocie na rynek pracy",
        "Przekwalifikowuję się": "do przekwalifikowania się"
    }
    const processGoal = goalMappings[formData.cel_wybor] || formData.cel_wybor;




    const getNextStepQuestion = (stepIndex) => {
        if (stepIndex === 0) {
            return `Jaki pierwszy krok możesz zrobić już dziś, by przybliżyć się do ${processGoal}?`;
        } else if (stepIndex === 1) {
            return "A w drugiej kolejności, co możesz zrobić?";
        } else if (stepIndex === 2) {
            return "I co jeszcze możesz zrobić?";
        } else {
            return "Co jeszcze warto zrobić?";
        }
    };

    const handleAddStep = () => {
        if (newStep.trim()) {
            setValue('plan_dzialania_kroki', [...steps, newStep.trim()]);
            setNewStep('');
        }
    };

    const handleRemoveStep = (index) => {
        setValue('plan_dzialania_kroki', steps.filter((_, i) => i !== index));
    };

    const handleEditStep = (index) => {
        setEditingIndex(index);
        setEditedValue(steps[index]);
    };

    const handleSaveEdit = () => {
        if (editedValue.trim()) {
            const updatedSteps = [...steps];
            updatedSteps[editingIndex] = editedValue.trim();
            setValue('plan_dzialania_kroki', updatedSteps);
            setEditingIndex(null);
            setEditedValue('');
        }
    };

    const handleCancelEdit = () => {
        setEditingIndex(null);
        setEditedValue('');
    };

    return (
        <Box component="form" onSubmit={handleSubmit(onNext)} sx={{ mt: 3 }}>
            <Typography variant="h6" color="black" gutterBottom>
                {getNextStepQuestion(steps.length)}
            </Typography>

            <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                    <TextField
                        fullWidth
                        value={newStep}
                        onChange={(e) => setNewStep(e.target.value)}
                        placeholder="Wpisz tutaj..."
                    />
                    <Button
                        onClick={handleAddStep}
                        disabled={!newStep.trim() || steps.length >= 15}
                        color="primary"
                    >
                        Dodaj krok
                    </Button>
                </Box>

                <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                    <List>
                        {steps.map((step, index) => (
                            <ListItem
                                key={index}
                                secondaryAction={
                                    editingIndex === index ? (
                                        <Box sx={{ display: 'flex', gap: 1 }}>
                                            <IconButton
                                                edge="end"
                                                aria-label="save"
                                                onClick={handleSaveEdit}
                                                color="primary"
                                            >
                                                <CheckIcon />
                                            </IconButton>
                                            <IconButton
                                                edge="end"
                                                aria-label="cancel"
                                                onClick={handleCancelEdit}
                                            >
                                                <CancelIcon />
                                            </IconButton>
                                        </Box>
                                    ) : (
                                        <Box sx={{ display: 'flex', gap: 1 }}>
                                            <IconButton
                                                edge="end"
                                                aria-label="edit"
                                                onClick={() => handleEditStep(index)}
                                            >
                                                <EditIcon />
                                            </IconButton>
                                            <IconButton
                                                edge="end"
                                                aria-label="delete"
                                                onClick={() => handleRemoveStep(index)}
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </Box>
                                    )
                                }
                            >
                                {editingIndex === index ? (
                                    <TextField
                                        fullWidth
                                        value={editedValue}
                                        onChange={(e) => setEditedValue(e.target.value)}
                                        size="small"
                                        autoFocus
                                    />
                                ) : (
                                    `${index + 1}. ${step}`
                                )}
                            </ListItem>
                        ))}
                    </List>
                </Paper>

                {errors.plan_dzialania_kroki && (
                    <FormHelperText error>
                        {errors.plan_dzialania_kroki.message}
                    </FormHelperText>
                )}
            </Box>

            {/* <Controller
                name="plan_dzialania_dodatkowe_mysli"
                control={control}
                render={({ field }) => (
                    <TextField
                        {...field}
                        fullWidth
                        multiline
                        rows={4}
                        label="Dodatkowe przemyślenia dotyczące Twojego planu działania (opcjonalne)"
                        sx={{ mb: 3 }}
                    />
                )}
            /> */}

            <Box sx={{ display: 'flex', gap: 2 }}>
                <IconButton
                    onClick={onBack}
                    disabled={isFirstSection}
                    sx={{
                        border: '1px solid',
                        borderColor: isFirstSection ? 'action.disabled' : 'primary.main',
                        borderRadius: 1
                    }}
                >
                    <ArrowBackIcon />
                </IconButton>
                <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    fullWidth
                    disabled={steps.length < 4}
                    endIcon={<ArrowForwardIcon />}
                >
                    Zakończ
                </Button>
            </Box>
        </Box>
    );
};

export default ActionPlanSection; 